import React, {Component} from "react";
import {Modal, ModalHeader, ModalBody, ModalFooter} from 'reactstrap';
import {APIClient} from "../../utils/APIClient";
import {API_URL} from "../../settings/local_settings";
import {ClientHeader} from "./ClientHeader";
import {ClientTabs} from "./ClientTabs";
import {Spinner} from "../Spinner";
import {FaIcon} from "../FaIcon";
import {ReportCard} from "../reports/ReportCard";
import {TemplatePickerModal} from "../reports/TemplatePickerModal";
import {AgentWebSocketsClient} from "../../websockets/WebSocketsClient";
import {withTranslation} from 'react-i18next';
import './ClientReportsContent.scss';


class ClientReportsContentClass extends Component {
    constructor(props) {
        super(props);
        this.state = {
            name: '',
            reports: [],
            loading: true,
            generating: false,
            showSuccessModal: false,
            showTemplateModal: false,
        };

        this.pollingInterval = null;
        this.handleGenerate = this.handleGenerate.bind(this);
        this.handleGenerateTemplate = this.handleGenerateTemplate.bind(this);
        this.handleDelete = this.handleDelete.bind(this);
        this.handleCancel = this.handleCancel.bind(this);
        this.handleToggleVisibility = this.handleToggleVisibility.bind(this);
        this.toggleSuccessModal = this.toggleSuccessModal.bind(this);
        this.toggleTemplateModal = this.toggleTemplateModal.bind(this);
    }

    componentDidMount() {
        this.getClientData();
        this.fetchReports();
        this.subscribePusher();
    }

    componentWillUnmount() {
        if (this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
        if (this.props.clientId) {
            AgentWebSocketsClient.unsubscribe(`client-reports-${this.props.clientId}`);
        }
    }

    subscribePusher() {
        const channel = AgentWebSocketsClient.subscribe(`client-reports-${this.props.clientId}`);
        if (!channel) return;

        channel.bind('report-status-changed', (data) => {
            this.setState(prevState => {
                const reports = prevState.reports.map(r => {
                    if (r.report_id === data.report_id) {
                        return {
                            ...r,
                            status: data.status,
                            url: data.url || r.url,
                        };
                    }
                    return r;
                });
                return {reports};
            }, () => {
                this.startPollingIfNeeded(this.state.reports);
            });
        });
    }

    startPollingIfNeeded(reports) {
        const hasProcessing = reports.some(r => r.status === 'PROCESSING');
        if (hasProcessing && !this.pollingInterval) {
            this.pollingInterval = setInterval(() => this.fetchReports(), 30000);
        } else if (!hasProcessing && this.pollingInterval) {
            clearInterval(this.pollingInterval);
            this.pollingInterval = null;
        }
    }

    getClientData() {
        new APIClient(API_URL + 'client/' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                this.setState({name: data.data['name']});
            }
        });
    }

    fetchReports() {
        this.setState({loading: true});
        new APIClient(API_URL + 'reports/?client_id=' + this.props.clientId).call('GET').then((data) => {
            if (data && data.status === 'OK') {
                const reports = data.data.reports || [];
                this.setState({reports, loading: false}, () => {
                    this.startPollingIfNeeded(reports);
                });
            } else {
                this.setState({loading: false});
            }
        });
    }

    handleGenerate() {
        this.setState({showTemplateModal: true});
    }

    toggleTemplateModal() {
        this.setState({showTemplateModal: !this.state.showTemplateModal});
    }

    handleGenerateTemplate(templateSlug, visibleToClients = true, isPublic = true, scope = null, userTemplateId = null) {
        this.setState({generating: true, showTemplateModal: false});
        const body = {
            action_type: 'template_report',
            client_id: this.props.clientId,
            is_visible_to_clients: visibleToClients,
            is_public: isPublic,
            data_source_type: scope?.data_source_type || null,
            ad_account_id: scope?.ad_account_id || null,
            business_unit_id: scope?.business_unit_id || null,
        };
        // Con una plantilla propia el picker deja templateSlug en null: mandar los dos
        // haría que el backend caiga al semanal oficial.
        if (userTemplateId) {
            body.user_template_id = userTemplateId;
        } else {
            body.template_slug = templateSlug;
        }
        new APIClient(API_URL + 'agent/trigger-action/').call('POST', body).then((data) => {
            this.setState({generating: false});
            if (data && data.status === 'OK') {
                const newReport = {
                    report_id: data.data.report_id,
                    title: this.props.t('client_reports.generating'),
                    generated_at: new Date().toISOString(),
                    status: 'PROCESSING',
                    url: null,
                    block_count: 0,
                    can_delete: true,
                };
                this.setState(prevState => ({
                    reports: [newReport, ...prevState.reports],
                    showSuccessModal: true,
                }), () => {
                    this.startPollingIfNeeded(this.state.reports);
                });
            } else if (data) {
                const {t} = this.props;
                // NETWORK_ERROR existe justamente para que un microcorte NO dispare un alert()
                // (ver el contrato en APIClient.js). El else de abajo lo agarraba igual.
                if (data.status === 'NETWORK_ERROR') return;
                if (data.status === 'ALREADY_PROCESSING') {
                    alert(t('client_reports.error_already_processing'));
                } else {
                    // El error_message del backend viene sin traducir (y a veces en castellano
                    // hardcodeado): al usuario le va una clave i18n, el crudo queda en consola.
                    console.error('[trigger-action]', data.status, data.error_message);
                    alert(t('client_reports.error_generic'));
                }
            }
        });
    }

    handleDelete(reportId) {
        const {t} = this.props;
        if (!window.confirm(t('client_reports.delete_confirm'))) return;

        new APIClient(API_URL + 'reports/' + reportId + '/').call('DELETE').then((data) => {
            if (data && data.status === 'OK') {
                this.setState(prevState => ({
                    reports: prevState.reports.filter(r => r.report_id !== reportId),
                }));
            } else if (data && data.status === 'FORBIDDEN') {
                alert(t('client_reports.delete_forbidden'));
            }
        });
    }

    handleCancel(reportId) {
        const {t} = this.props;
        if (!window.confirm(t('client_reports.cancel_confirm'))) return;

        new APIClient(API_URL + 'reports/' + reportId + '/').call('DELETE').then((data) => {
            if (data && (data.status === 'OK' || data.status === 'FORBIDDEN')) {
                this.fetchReports();
            }
        });
    }

    handleToggleVisibility(reportId, updates) {
        new APIClient(API_URL + 'reports/' + reportId + '/').call('PATCH', updates).then((data) => {
            if (data && data.status === 'OK') {
                this.setState(prevState => ({
                    reports: prevState.reports.map(r =>
                        r.report_id === reportId ? {...r, ...updates} : r
                    ),
                }));
            }
        });
    }

    toggleSuccessModal() {
        this.setState({showSuccessModal: !this.state.showSuccessModal});
    }

    renderReportCard(report) {
        const {t} = this.props;
        return (
            <ReportCard
                key={report.report_id}
                report={report}
                onDelete={this.handleDelete}
                onCancel={this.handleCancel}
                onToggleVisibility={this.handleToggleVisibility}
                showClientName={false}
                t={t}
                language={this.props.i18n.language}
            />
        );
    }

    renderContent() {
        const {t} = this.props;
        const {reports, loading} = this.state;

        if (loading) {
            return (
                <div className="client-reports-loading">
                    <Spinner color="positive"/>
                </div>
            );
        }

        if (reports.length === 0) {
            return (
                <div className="client-reports-empty">
                    <div className="client-reports-empty-icon">
                        <FaIcon iconName="wand-magic-sparkles" />
                    </div>
                    <h3 className="client-reports-empty-title">{t('client_reports.no_reports')}</h3>
                    <p className="client-reports-empty-subtitle">{t('client_reports.empty_subtitle')}</p>
                    <button
                        className="btn -alt client-reports-empty-cta"
                        onClick={this.handleGenerate}
                        disabled={this.state.generating}
                    >
                        <FaIcon iconName="plus" />
                        {t('client_reports.empty_cta')}
                    </button>
                </div>
            );
        }

        return (
            <div className="client-reports-list">
                {reports.map(report => this.renderReportCard(report))}
            </div>
        );
    }

    render() {
        const {t} = this.props;
        const {generating} = this.state;
        const hasProcessing = this.state.reports.some(r => r.status === 'PROCESSING');

        return (
            <section className="section-content">
                <ClientHeader name={this.state.name}/>
                <ClientTabs clientId={this.props.clientId}/>

                <div className="section-body">
                    <div className="box client-reports-box">
                        <header className="client-reports-box-header">
                            <div className="client-reports-header-content">
                                <h2 className="client-reports-title">{t('client_reports.title')}</h2>
                                <p className="client-reports-subtitle">{t('client_reports.subtitle')}</p>
                            </div>
                            <div className="client-reports-generate-btn">
                                <button
                                    className="btn -alt"
                                    onClick={this.handleGenerate}
                                    disabled={generating || hasProcessing}
                                >
                                    <FaIcon iconName="wand-magic-sparkles" />
                                    {generating ? t('client_reports.generating') : t('reports.generate_report')}
                                </button>
                            </div>
                        </header>

                        {this.renderContent()}
                    </div>
                </div>

                <TemplatePickerModal
                    isOpen={this.state.showTemplateModal}
                    toggle={this.toggleTemplateModal}
                    clientId={this.props.clientId}
                    onGenerate={this.handleGenerateTemplate}
                    generating={this.state.generating}
                />

                <Modal isOpen={this.state.showSuccessModal} toggle={this.toggleSuccessModal} className="client-reports-success-modal">
                    <ModalHeader toggle={this.toggleSuccessModal}>
                        {t('client_reports.success_title')}
                    </ModalHeader>
                    <ModalBody className="client-reports-success-modal-body">
                        {t('client_reports.success_message')}
                    </ModalBody>
                    <ModalFooter className="client-reports-success-modal-footer">
                        <button className="btn -alt" onClick={this.toggleSuccessModal}>
                            {t('client_reports.success_ok')}
                        </button>
                    </ModalFooter>
                </Modal>
            </section>
        );
    }
}

export const ClientReportsContent = withTranslation()(ClientReportsContentClass);
